import { createFileRoute, ErrorComponent } from "@tanstack/react-router";
import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { useAuth } from "@clerk/tanstack-react-start";
import { useState, type ReactNode } from "react";
import { format } from "date-fns";
import { Calendar, Clock, Loader2, Monitor } from "lucide-react";
import {
  getSessionDataOptions,
  getSessionRegistrationStateOptions,
  registerForSession,
  unregisterFromSession,
} from "~/lib/functions/summit";
import { Card, CardContent } from "~/components/ui/card";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { AuthorCard } from "~/components/ui/author-card";
import { SummitRegistration } from "~/components/summit/summit-registration";
import { MarkdownContent } from "~/components/markdown-content";
import { WaveBackground } from "~/components/ui/wave-background";
import { createBrandMeta } from "~/lib/shared/meta";
import { Separator } from "~/components/ui/separator";
import { formatSummitSessionFormat } from "~/lib/shared/utils/format-session-format";
import type { SummitSession } from "~/lib/shared/types/summit";
import {
  SUMMIT_SPOT_SECURED_REDIRECT_URL,
  SUMMIT_VIP_UPSELL_URL,
  SUMMIT_WORKSHOP_REGISTRATION_LIMIT,
} from "~/lib/shared/constants/summit";

export const Route = createFileRoute(
  "/_public/summit/$summitId/session/$sessionId",
)({
  loader: ({ context, params }) => {
    return context.queryClient.ensureQueryData(
      getSessionDataOptions(params.sessionId),
    );
  },
  head: ({ loaderData, params }) => {
    const { session, summit } = loaderData || {};

    if (!session) {
      return createBrandMeta({
        title: "Summit Session",
        description:
          "Join us for an exclusive summit session with industry experts.",
        ogType: "website",
      });
    }

    // Generate description combining session and speaker info
    const speakerInfo = session.speaker.title
      ? `${session.speaker.name}, ${session.speaker.title}`
      : session.speaker.name;

    const description = session.fullDescription || session.description;
    const enhancedDescription = `${description} Join ${speakerInfo} for this ${formatSummitSessionFormat(session.sessionFormat)} session.`;

    // Generate keywords from session data
    const keywords = [
      "summit",
      "session",
      "coaching",
      session.sessionFormat || "presentation",
      session.speaker.name,
      session.speaker.title || "",
      ...session.topics,
    ]
      .filter(Boolean)
      .filter((keyword): keyword is string => typeof keyword === "string");

    // Session URL
    const sessionUrl = `https://dreamcoachmatch.com/summit/${params.summitId}/session/${params.sessionId}`;

    // Use custom session social image
    const ogImage = `https://dreamcoachmatch.com/summit/${params.summitId}/session/${params.sessionId}/social-image.svg`;

    const metaTags = createBrandMeta({
      title: session.title,
      description: enhancedDescription,
      ogTitle: `${session.title} | ${summit?.title || "Summit"}`,
      ogDescription: description,
      ogImage,
      ogType: "website",
      ogUrl: sessionUrl,
      twitterCard: "summary_large_image",
      twitterTitle: session.title,
      twitterDescription: description,
      twitterImage: ogImage,
      canonical: sessionUrl,
      keywords,
      author: session.speaker.name,
    });

    // Create Event structured data
    const eventStructuredData = {
      "@context": "https://schema.org",
      "@type": "Event",
      name: session.title,
      description: description,
      startDate: new Date(
        `${session.date.toISOString().split("T")[0]}T${convertTimeToISO(session.startTime)}`,
      ).toISOString(),
      endDate: new Date(
        `${session.date.toISOString().split("T")[0]}T${convertTimeToISO(session.endTime)}`,
      ).toISOString(),
      eventStatus: "https://schema.org/EventScheduled",
      eventAttendanceMode: "https://schema.org/OnlineEventAttendanceMode",
      location: {
        "@type": "VirtualLocation",
        url: sessionUrl,
      },
      performer: {
        "@type": "Person",
        name: session.speaker.name,
        jobTitle: session.speaker.title,
        image: session.speaker.avatarUrl,
        ...(session.speaker.bio && { description: session.speaker.bio }),
        ...(session.speaker.linkedInUrl && {
          sameAs: [
            session.speaker.linkedInUrl,
            session.speaker.twitterUrl,
            session.speaker.websiteUrl,
          ].filter(Boolean),
        }),
      },
      organizer: {
        "@type": "Organization",
        name: "Dream Coach Match",
        url: "https://dreamcoachmatch.com",
      },
      ...(session.topics.length > 0 && {
        about: session.topics.map((topic) => ({
          "@type": "Thing",
          name: topic,
        })),
      }),
    };

    return {
      ...metaTags,
      scripts: [
        {
          type: "application/ld+json",
          innerHTML: JSON.stringify(eventStructuredData),
        },
      ],
    };
  },
  component: SessionPage,
  errorComponent: ({ error }) => <ErrorComponent error={error} />,
});

const convertTimeToISO = (timeString: string): string => {
  // Convert "10:00 AM" or "2:30 PM" format to "10:00:00" or "14:30:00"
  const [time, period] = timeString.split(" ");
  const [hours, minutes] = time.split(":").map(Number);

  let hour24 = hours;
  if (period === "PM" && hours !== 12) {
    hour24 += 12;
  } else if (period === "AM" && hours === 12) {
    hour24 = 0;
  }

  return `${hour24.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:00`;
};

interface SessionRegistrationStepProps {
  sessionId: string;
  sessionTitle: string;
  sessionFormat?: SummitSession["sessionFormat"];
}

function SessionPage() {
  const { sessionId } = Route.useParams();
  const { data } = useSuspenseQuery(getSessionDataOptions(sessionId));
  const { session } = data;
  const sessionFormatLabel = formatSummitSessionFormat(session.sessionFormat);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative">
        <WaveBackground />

        {/* Overlay for better contrast */}
        <div className="absolute inset-0 bg-black/20" />

        <div className="mx-auto max-w-4xl px-6 py-12 z-10 relative">
          {/* Session metadata */}
          <div className="mb-6 flex flex-wrap items-center gap-3 z-10">
            {session.sessionFormat && (
              <Badge variant="secondary" className="gap-1.5">
                <Monitor className="h-3 w-3" />
                {sessionFormatLabel}
              </Badge>
            )}
            <div className="flex items-center gap-1.5 text-sm text-white/80">
              <Clock className="h-3.5 w-3.5" />
              <span>
                {session.startTime} - {session.endTime} {session.timezone}
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-sm text-white/80">
              <Calendar className="h-3.5 w-3.5" />
              <span>{format(session.date, "EEEE, MMMM d, yyyy")}</span>
            </div>
          </div>

          {/* Session title */}
          <div className="mt-12 mb-8 flex flex-row justify-between items-center gap-4">
            <h1 className="font-heading max-w-xl text-balance text-4xl md:text-5xl tracking-tight text-white lg:text-6xl">
              {session.title}
            </h1>
            <a href="#register">
              <Button variant="secondary" className="shadow-lg">
                Register
              </Button>
            </a>
          </div>

          {/* Speaker section */}
          <Card className="mb-8">
            <CardContent>
              <AuthorCard
                author={session.speaker}
                variant="large"
                showBio={true}
                showSocialLinks={true}
                titleLevel="h2"
              />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Content Section */}
      <div className="mx-auto max-w-4xl px-6 py-12">
        <div className="grid gap-12 lg:grid-cols-3">
          {/* Main content */}
          <div className="lg:col-span-2">
            <div className="space-y-8">
              {/* Session description */}
              <div>
                <h3 className="mb-4 text-2xl font-semibold text-gray-900">
                  About This Session
                </h3>
                {session.fullDescription && (
                  <MarkdownContent
                    content={session.fullDescription}
                    className="prose"
                  />
                )}
              </div>

              {/* Topics covered */}
              {session.topics.length > 0 && (
                <div>
                  <h3 className="mb-4 text-2xl font-semibold text-gray-900">
                    Topics Covered
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {session.topics.map((topic) => (
                      <Badge key={topic} variant="secondary" size="lg">
                        {topic}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <Card>
                <CardContent className="space-y-10">
                  <a id="register" className="anchor -mt-12" />
                  <section>
                    <div className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.24em] text-purple-600">
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-100 text-base text-purple-700">
                        1
                      </span>
                      Step 1 · Summit registration
                    </div>
                    <p className="mt-3 text-sm text-gray-600">
                      Save your complimentary pass to unlock every keynote,
                      panel, and networking moment.
                    </p>
                    <div className="mt-6">
                      <SummitRegistration
                        redirectUrl={SUMMIT_SPOT_SECURED_REDIRECT_URL}
                      />
                    </div>
                  </section>

                  <Separator />

                  <SessionRegistrationStep
                    sessionId={session.id}
                    sessionTitle={session.title}
                    sessionFormat={session.sessionFormat}
                  />
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Sidebar - Registration CTA */}
          <div className="lg:col-span-1">
            <div className="sticky top-8">
              {/* Additional session info */}
              <Card className="mt-6">
                <CardContent>
                  <h4 className="mb-4 font-semibold text-gray-900">
                    Session Details
                  </h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Format</span>
                      <span className="font-medium">{sessionFormatLabel}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Duration</span>
                      <span className="font-medium">
                        {session.startTime} - {session.endTime}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Date</span>
                      <span className="font-medium">
                        {format(session.date, "MMM d, yyyy")}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SessionRegistrationStep({
  sessionId,
  sessionTitle,
  sessionFormat,
}: SessionRegistrationStepProps) {
  const { isSignedIn } = useAuth();
  const queryClient = useQueryClient();
  const [actionError, setActionError] = useState<string | null>(null);

  const registrationQuery = useQuery({
    ...getSessionRegistrationStateOptions(sessionId),
    enabled: isSignedIn,
  });

  const registerMutation = useMutation({
    mutationFn: registerForSession,
    onMutate: () => setActionError(null),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["session", sessionId, "registration-state"],
      });
      queryClient.invalidateQueries({ queryKey: ["summit", "active"] });
    },
    onError: (error) => {
      setActionError(
        error instanceof Error
          ? error.message
          : "We couldn't save your seat. Please try again.",
      );
    },
  });

  const unregisterMutation = useMutation({
    mutationFn: unregisterFromSession,
    onMutate: () => setActionError(null),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["session", sessionId, "registration-state"],
      });
      queryClient.invalidateQueries({ queryKey: ["summit", "active"] });
    },
    onError: (error) => {
      setActionError(
        error instanceof Error
          ? error.message
          : "We couldn't update your registration right now. Please try again.",
      );
    },
  });

  const registration = registrationQuery.data?.registration;
  const meetingUrl = registrationQuery.data?.session.meetingUrl ?? null;
  const isWorkshop = registration?.isWorkshop ?? false;
  const limitReached = registration?.limitReached ?? false;
  const registrationLimit =
    registration?.workshopRegistrationLimit ??
    SUMMIT_WORKSHOP_REGISTRATION_LIMIT;
  const workshopRegistrationsCount =
    registration?.workshopRegistrationsCount ?? 0;
  const sessionFormatLabel = formatSummitSessionFormat(sessionFormat);
  const handleRegister = () => registerMutation.mutate({ data: { sessionId } });
  const handleCancelRegistration = () =>
    unregisterMutation.mutate({ data: { sessionId } });

  const content: ReactNode = (() => {
    if (!isSignedIn) {
      return <NotSignedInView sessionFormatLabel={sessionFormatLabel} />;
    }

    if (registrationQuery.isLoading || registrationQuery.isFetching) {
      return <LoadingRegistrationView />;
    }

    if (!registration) {
      return <RegistrationUnavailableView />;
    }

    if (!registration.summit.isRegistered) {
      return <SummitRegistrationRequiredView />;
    }

    if (registration.requiresVip && !registration.summit.isVip) {
      return (
        <VipUpsellView
          isWorkshop={isWorkshop}
          registrationLimit={registrationLimit}
          sessionFormatLabel={sessionFormatLabel}
        />
      );
    }

    if (registration.session.isRegistered) {
      return (
        <AlreadyRegisteredView
          isWorkshop={isWorkshop}
          meetingUrl={meetingUrl}
          onCancel={handleCancelRegistration}
          registrationLimit={registrationLimit}
          sessionTitle={sessionTitle}
          workshopRegistrationsCount={workshopRegistrationsCount}
          isCancelling={unregisterMutation.isPending}
        />
      );
    }

    return (
      <RegistrationCallToActionView
        isRegistering={registerMutation.isPending}
        isWorkshop={isWorkshop}
        limitReached={limitReached}
        onRegister={handleRegister}
        registrationLimit={registrationLimit}
        sessionFormatLabel={sessionFormatLabel}
        sessionTitle={sessionTitle}
        workshopRegistrationsCount={workshopRegistrationsCount}
      />
    );
  })();

  return (
    <section>
      <div className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.24em] text-purple-600">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-100 text-base text-purple-700">
          2
        </span>
        Step 2 · Claim your seat
      </div>
      <p className="mt-3 text-sm text-gray-600">
        Choose your spot for the {sessionFormatLabel.toLowerCase()} and we'll
        send the details straight to your inbox.
      </p>
      <div className="mt-6 space-y-4">
        {content}
        {actionError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {actionError}
          </div>
        )}
      </div>
    </section>
  );
}

interface NotSignedInViewProps {
  sessionFormatLabel: string;
}

function NotSignedInView({ sessionFormatLabel }: NotSignedInViewProps) {
  return (
    <div className="rounded-lg border border-dashed border-purple-200 bg-white p-6 text-sm text-gray-600">
      <p>
        Sign in or complete Step 1 above to reserve your seat for this{" "}
        {sessionFormatLabel.toLowerCase()}.
      </p>
      <p className="mt-2 text-xs text-gray-500">
        Already registered for the summit? Log in to manage your sessions.
      </p>
    </div>
  );
}

function LoadingRegistrationView() {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-purple-100 bg-white p-6 text-sm text-gray-600">
      <Loader2 className="h-4 w-4 animate-spin text-purple-600" />
      Checking your session access...
    </div>
  );
}

function RegistrationUnavailableView() {
  return (
    <div className="rounded-lg border border-dashed border-purple-200 bg-white p-6 text-sm text-gray-600">
      <p>
        We couldn't confirm your session access. Please refresh and try again.
      </p>
    </div>
  );
}

function SummitRegistrationRequiredView() {
  return (
    <div className="rounded-lg border border-dashed border-purple-200 bg-white p-6 text-sm text-gray-600">
      <p>Register for the summit first to unlock session registration.</p>
    </div>
  );
}

interface VipUpsellViewProps {
  sessionFormatLabel: string;
  isWorkshop: boolean;
  registrationLimit: number;
}

function VipUpsellView({
  sessionFormatLabel,
  isWorkshop,
  registrationLimit,
}: VipUpsellViewProps) {
  return (
    <div className="space-y-4 rounded-lg border border-purple-100 bg-white p-6 text-sm text-gray-700">
      <p>
        This {sessionFormatLabel.toLowerCase()} is reserved for VIP members.
      </p>
      <Button
        variant="orange"
        className="w-full sm:w-auto"
        onClick={() => window.open(SUMMIT_VIP_UPSELL_URL, "_blank")}
      >
        Upgrade to VIP
      </Button>
      {isWorkshop && (
        <p className="text-xs text-gray-500">
          VIP members can claim up to {registrationLimit} workshops.
        </p>
      )}
    </div>
  );
}

interface AlreadyRegisteredViewProps {
  sessionTitle: string;
  meetingUrl: string | null;
  isWorkshop: boolean;
  workshopRegistrationsCount: number;
  registrationLimit: number;
  onCancel: () => void;
  isCancelling: boolean;
}

function AlreadyRegisteredView({
  sessionTitle,
  meetingUrl,
  isWorkshop,
  workshopRegistrationsCount,
  registrationLimit,
  onCancel,
  isCancelling,
}: AlreadyRegisteredViewProps) {
  return (
    <div className="space-y-4 rounded-lg border border-green-200 bg-green-50 p-6 text-sm text-green-900">
      <div>
        <p className="font-semibold">You're all set for {sessionTitle}.</p>
        {isWorkshop && (
          <p className="mt-1 text-xs text-green-800">
            You've reserved {workshopRegistrationsCount}/{registrationLimit}{" "}
            workshop spots.
          </p>
        )}
      </div>
      <div className="flex flex-col gap-3 sm:flex-row">
        {meetingUrl && (
          <Button
            variant="default"
            className="w-full sm:w-auto"
            onClick={() => window.open(meetingUrl, "_blank")}
          >
            Join Live Session
          </Button>
        )}
        <Button
          variant="outline"
          className="w-full sm:w-auto"
          loading={isCancelling}
          onClick={onCancel}
        >
          Cancel Registration
        </Button>
      </div>
    </div>
  );
}

interface RegistrationCallToActionViewProps {
  sessionTitle: string;
  sessionFormatLabel: string;
  isWorkshop: boolean;
  registrationLimit: number;
  workshopRegistrationsCount: number;
  limitReached: boolean;
  onRegister: () => void;
  isRegistering: boolean;
}

function RegistrationCallToActionView({
  sessionTitle,
  sessionFormatLabel,
  isWorkshop,
  registrationLimit,
  workshopRegistrationsCount,
  limitReached,
  onRegister,
  isRegistering,
}: RegistrationCallToActionViewProps) {
  const helperText = limitReached
    ? `You've already registered for ${workshopRegistrationsCount}/${registrationLimit} workshops. Cancel an existing workshop to free up a spot.`
    : isWorkshop
      ? `VIP members can save a seat in up to ${registrationLimit} workshops.`
      : "You're moments away from confirming your seat.";

  return (
    <div className="space-y-4 rounded-lg border border-purple-100 bg-white p-6 text-sm text-gray-700">
      <p>
        Reserve your seat for <strong>{sessionTitle}</strong>.
      </p>
      <Button
        className="w-full sm:w-auto"
        loading={isRegistering}
        disabled={isRegistering || limitReached}
        onClick={onRegister}
      >
        {limitReached
          ? "Workshop limit reached"
          : `Register for this ${sessionFormatLabel.toLowerCase()}`}
      </Button>
      <p className="text-xs text-gray-500">{helperText}</p>
      {isWorkshop && (
        <p className="text-xs text-gray-500">
          You've registered for {workshopRegistrationsCount}/{registrationLimit}{" "}
          workshops.
        </p>
      )}
    </div>
  );
}
